import { 
  collection, 
  getDocs, 
  query, 
  where, 
  orderBy,
  limit,
  onSnapshot
} from "firebase/firestore";
import { db } from "../firebase/firebase";

// Define EmhubAccessPass interface
export interface EmhubAccessPass {
  id: string;
  Qrid: string;
  qrid?: string;
  "Emhub Access": string; // URL for access pass
  accessUrl?: string;
  "Contact Number"?: string;
  contactNumber?: string;
  Date?: string;
  targetDate?: string;
  pickupDate?: string;
  slotStart?: string;
  Type?: string; // Can be "Collection", "Return", "User Request Collection", "User Request Return", "Others"
  mode?: string;
  status?: string;
  customerName?: string;
  source?: string;
  orderId?: string;
  createdAt?: any;
  updatedAt?: any;
  IP?: string;
  Location?: string;
  "Document ID"?: string;
  Timestamp?: any;
  [key: string]: any; // For any additional fields
}

const ACCESS_COLLECTION = "EmhubAccessPreGen";

const normalizeAccessPass = (id: string, data: any): EmhubAccessPass => {
  const normalizedQrid = data.qrid || data.Qrid || "";
  const normalizedUrl = data.accessUrl || data["Emhub Access"] || "";
  const normalizedDate = data.targetDate || data.pickupDate || data.Date || "";

  return {
    id,
    ...data,
    qrid: normalizedQrid,
    Qrid: normalizedQrid,
    accessUrl: normalizedUrl,
    "Emhub Access": normalizedUrl,
    Date: normalizedDate,
    Type: data.Type || data.mode || data.ruleMatched || "Other"
  } as EmhubAccessPass;
};

// Get all Emhub Access Passes by Qrid
export const getEmhubAccessPassesByQrid = async (qrid: string): Promise<EmhubAccessPass[]> => {
  try {
    console.log(`Searching for Emhub Access Passes with Qrid: ${qrid}`);
    
    // Trim the Qrid to handle any whitespace issues
    const trimmedQrid = qrid.trim();
    
    try {
      // First try with proper index (after deployment)
      const indexedQuery = query(
        collection(db, ACCESS_COLLECTION),
        where("qrid", "==", trimmedQrid),
        orderBy("createdAt", "desc")
      );
      
      const querySnapshot = await getDocs(indexedQuery);
      console.log(`Found ${querySnapshot.size} Emhub Access Passes with Qrid: ${trimmedQrid}`);
      
      // Convert query results to EmhubAccessPass objects
      const accessPasses: EmhubAccessPass[] = [];
      querySnapshot.forEach((doc) => {
        accessPasses.push(normalizeAccessPass(doc.id, doc.data()));
      });
      
      return accessPasses;
      
    } catch (indexError) {
      console.log("Index not yet available, falling back to simple query:", indexError);
      
      // Fallback to simple query without orderBy (doesn't require composite index)
      const simpleQuery = query(
        collection(db, ACCESS_COLLECTION),
        where("qrid", "==", trimmedQrid)
      );
      
      const querySnapshot = await getDocs(simpleQuery);
      console.log(`Found ${querySnapshot.size} Emhub Access Passes with Qrid: ${trimmedQrid} (using fallback)`);
      
      // Filter and sort in memory
      const accessPasses: EmhubAccessPass[] = [];
      querySnapshot.forEach((doc) => {
        accessPasses.push(normalizeAccessPass(doc.id, doc.data()));
      });
      
      // Sort by Date manually 
      return accessPasses.sort((a, b) => {
        const aTime = a.createdAt?.toMillis?.() || 0;
        const bTime = b.createdAt?.toMillis?.() || 0;
        return bTime - aTime;
      });
    }
  } catch (error) {
    console.error("Error getting Emhub Access Passes:", error);
    return [];
  }
};

/**
 * Listen for real-time updates to EmhubAccessPass records with the given Qrid
 * @param qrid The Qrid to filter by
 * @param callback Function to call when the data changes
 * @returns Unsubscribe function to stop listening
 */
export const listenToEmhubAccessPasses = (
  qrid: string,
  callback: (passes: EmhubAccessPass[]) => void
): (() => void) => {
  console.log(`Setting up real-time listener for Emhub Access Passes with Qrid: ${qrid}`);
  
  // Trim the Qrid to handle any whitespace issues
  const trimmedQrid = qrid.trim();
  
  try {
    // Try with proper index first
    const indexedQuery = query(
      collection(db, ACCESS_COLLECTION),
      where("qrid", "==", trimmedQrid),
      orderBy("createdAt", "desc")
    );
    
    // Set up the listener
    return onSnapshot(
      indexedQuery,
      (snapshot) => {
        console.log(`Real-time update: ${snapshot.size} Emhub Access Passes with Qrid: ${trimmedQrid}`);
        
        // Convert query results to EmhubAccessPass objects
        const accessPasses: EmhubAccessPass[] = [];
        snapshot.forEach((doc) => {
          accessPasses.push(normalizeAccessPass(doc.id, doc.data()));
        });
        
        // Call the callback with the updated data
        callback(accessPasses);
      },
      (error) => {
        console.error("Error in real-time listener for Emhub Access Passes:", error);
        
        // Try fallback approach without orderBy if there's an index error
        if (error.code === 'failed-precondition' || error.message.includes('index')) {
          console.log("Index not available for real-time listener, using fallback");
          
          // Fallback to simple query without orderBy
          const simpleQuery = query(
            collection(db, ACCESS_COLLECTION),
            where("qrid", "==", trimmedQrid)
          );
          
          // Set up fallback listener
          return onSnapshot(
            simpleQuery,
            (snapshot) => {
              console.log(`Real-time update (fallback): ${snapshot.size} Emhub Access Passes`);
              
              // Convert query results to EmhubAccessPass objects
              const accessPasses: EmhubAccessPass[] = [];
              snapshot.forEach((doc) => {
                accessPasses.push(normalizeAccessPass(doc.id, doc.data()));
              });
              
              // Sort manually since we're not using orderBy in the query
              accessPasses.sort((a, b) => {
                const aTime = a.createdAt?.toMillis?.() || 0;
                const bTime = b.createdAt?.toMillis?.() || 0;
                return bTime - aTime;
              });
              
              // Call the callback with the sorted data
              callback(accessPasses);
            },
            (fallbackError) => {
              console.error("Error in fallback real-time listener:", fallbackError);
              // If even the fallback fails, return empty array
              callback([]);
            }
          );
        } else {
          // For other errors, return empty array
          callback([]);
        }
      }
    );
  } catch (setupError) {
    console.error("Failed to set up real-time listener:", setupError);
    // Return a no-op function since we couldn't set up a listener
    return () => {};
  }
};

// Get filtered Emhub Access Passes by Qrid and Type
export const getEmhubAccessPassesByQridAndType = async (
  qrid: string, 
  type?: string
): Promise<EmhubAccessPass[]> => {
  try {
    console.log(`Searching for Emhub Access Passes with Qrid: ${qrid} and Type: ${type || 'any'}`);
    
    // Trim the Qrid to handle any whitespace issues
    const trimmedQrid = qrid.trim();
    
    try {
      // Try with proper index first (if type is provided)
      if (type) {
        const indexedQuery = query(
          collection(db, ACCESS_COLLECTION),
          where("qrid", "==", trimmedQrid),
          orderBy("createdAt", "desc")
        );
        
        const querySnapshot = await getDocs(indexedQuery);
        console.log(`Found ${querySnapshot.size} Emhub Access Passes with Qrid: ${trimmedQrid} and Type: ${type}`);
        
        const accessPasses: EmhubAccessPass[] = [];
        querySnapshot.forEach((doc) => {
          const normalized = normalizeAccessPass(doc.id, doc.data());
          if (normalized.Type === type) {
            accessPasses.push(normalized);
          }
        });
        
        return accessPasses;
      } else {
        // If no type filter, use the simpler query with just Qrid and Date
        return getEmhubAccessPassesByQrid(qrid);
      }
    } catch (indexError) {
      console.log("Index not yet available, falling back to simple query:", indexError);
      
      // Fallback to simple query without orderBy (doesn't require composite index)
      const simpleQuery = query(
        collection(db, ACCESS_COLLECTION),
        where("qrid", "==", trimmedQrid)
      );
      
      const querySnapshot = await getDocs(simpleQuery);
      console.log(`Found ${querySnapshot.size} Emhub Access Passes with Qrid: ${trimmedQrid} (using fallback)`);
      
      // Filter by type and sort in memory
      const accessPasses: EmhubAccessPass[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        // Apply type filtering in memory if type is specified
        const normalized = normalizeAccessPass(doc.id, data);
        if (!type || normalized.Type === type) {
          accessPasses.push(normalized);
        }
      });
      
      // Sort by Date manually 
      return accessPasses.sort((a, b) => {
        const aTime = a.createdAt?.toMillis?.() || 0;
        const bTime = b.createdAt?.toMillis?.() || 0;
        return bTime - aTime;
      });
    }
  } catch (error) {
    console.error("Error getting filtered Emhub Access Passes:", error);
    console.log("Please create the required indexes for the Emhub Access Pass collection using Firebase CLI or console.");
    return [];
  }
};

// Get unique types from Emhub Access Passes
export const getEmhubAccessPassTypes = async (): Promise<string[]> => {
  try {
    // Query a limited set of access passes to extract types
    const accessPassQuery = query(
      collection(db, ACCESS_COLLECTION),
      limit(100)
    );
    
    const querySnapshot = await getDocs(accessPassQuery);
    
    // Extract unique types
    const typesSet = new Set<string>();
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      if (data.Type) {
        typesSet.add(data.Type);
      }
    });
    
    return Array.from(typesSet);
  } catch (error) {
    console.error("Error getting Emhub Access Pass types:", error);
    return [];
  }
}; 
